import { useState, type FormEvent } from 'react'
import { toast } from 'react-toastify'
import { Accordion } from '../../components/accordion'
import { Button } from '../../components/button'
import { TextInput } from '../../components/inputs/textInput'
import { SelectProductModal } from '../../components/modal/app_modals/selectProductModal'
import { usePermissions } from '../../hooks/usePermissions'
import { getInterestErrorMessage } from '../../redux-toolkits/interests/interests.helpers'
import {
  useDeleteCreatorVideoMutation,
  useGetCreatorVideosQuery,
  useSaveCreatorVideoMutation,
  type CreatorVideo,
  type CreatorVideoBody,
  type LinkedProduct,
} from '../../redux-toolkits/discover/discover-slice'

const emptyVideo: CreatorVideoBody = {
  title: '',
  youtubeId: '',
  productIds: [],
  isActive: true,
  displayOrder: 0,
}

function youtubeIdFromInput(input: string): string | undefined {
  const value = input.trim()
  if (/^[\w-]{11}$/.test(value)) return value
  try {
    const url = new URL(value)
    let id: string | null | undefined
    if (url.hostname === 'youtu.be') id = url.pathname.split('/')[1]
    else if (
      ['youtube.com', 'www.youtube.com', 'm.youtube.com'].includes(url.hostname)
    ) {
      id = url.searchParams.get('v')
      if (['shorts', 'embed'].includes(url.pathname.split('/')[1]))
        id = url.pathname.split('/')[2]
    }
    return id !== undefined && id !== null && /^[\w-]{11}$/.test(id)
      ? id
      : undefined
  } catch {
    return undefined
  }
}

export function CreatorVideos({ creatorId }: { creatorId: string }) {
  const { hasPermissions } = usePermissions()
  const canEdit = hasPermissions(['update_vendor'])
  const {
    data: videos,
    isLoading,
    isError,
    refetch,
  } = useGetCreatorVideosQuery(creatorId)
  const [saveVideo, { isLoading: saving }] = useSaveCreatorVideoMutation()
  const [deleteVideo, { isLoading: deleting }] = useDeleteCreatorVideoMutation()
  const [editingId, setEditingId] = useState<string>()
  const [form, setForm] = useState<CreatorVideoBody>(emptyVideo)
  const [products, setProducts] = useState<LinkedProduct[]>([])
  const [selecting, setSelecting] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [removingId, setRemovingId] = useState<string>()

  const editVideo = (video?: CreatorVideo) => {
    setEditingId(video?._id)
    setForm(
      video === undefined
        ? emptyVideo
        : {
            youtubeId: video.youtubeId,
            title: video.title,
            productIds: video.productIds,
            isActive: video.isActive,
            displayOrder: video.displayOrder,
          },
    )
    setProducts(video?.linkedProducts ?? [])
    setShowForm(true)
  }
  const save = async (event: FormEvent) => {
    event.preventDefault()
    const youtubeId = youtubeIdFromInput(form.youtubeId)
    if (youtubeId === undefined) {
      toast.error('Enter a valid YouTube URL or video ID.')
      return
    }
    if (form.productIds.length === 0) {
      toast.error('Select at least one product.')
      return
    }
    try {
      await saveVideo({
        creatorId,
        videoId: editingId,
        body: { ...form, youtubeId, title: form.title.trim() },
      }).unwrap()
      toast.success('Creator video saved')
      setShowForm(false)
    } catch (error) {
      toast.error(getInterestErrorMessage(error))
    }
  }
  const remove = async (videoId: string) => {
    try {
      await deleteVideo({ creatorId, videoId }).unwrap()
      setRemovingId(undefined)
      if (editingId === videoId) setShowForm(false)
      toast.success('Creator video removed')
    } catch (error) {
      toast.error(getInterestErrorMessage(error))
    }
  }

  return (
    <Accordion title="Product videos">
      <div className="space-y-5">
        <p className="text-sm text-pry-gray">
          Link YouTube videos to this creator’s products. Active videos appear
          on the creator page and can be used in Discover.
        </p>
        {isLoading && <p>Loading videos…</p>}
        {isError && (
          <p role="alert">
            Could not load videos.{' '}
            <button
              type="button"
              onClick={() => {
                void refetch()
              }}
            >
              Retry
            </button>
          </p>
        )}
        {videos?.length === 0 && <p>No videos added yet.</p>}
        <div className="grid gap-4 md:grid-cols-2">
          {videos?.map((video) => (
            <div key={video._id} className="rounded-xl border p-4 space-y-3">
              <a href={video.watchUrl} target="_blank" rel="noreferrer">
                <img
                  src={video.thumbnailUrl}
                  alt={video.title}
                  className="aspect-video w-full rounded-lg object-cover"
                />
              </a>
              <p className="font-medium">{video.title}</p>
              <p className="text-sm">
                {video.isActive ? 'Active' : 'Hidden'} ·{' '}
                {video.productIds.length} linked products · Order{' '}
                {video.displayOrder}
              </p>
              <p className="text-sm">
                {video.linkedProducts.map((product) => product.name).join(', ')}
              </p>
              {canEdit && (
                <div className="flex gap-3">
                  <button type="button" onClick={() => editVideo(video)}>
                    Edit
                  </button>
                  <button
                    type="button"
                    className="text-danger"
                    onClick={() => setRemovingId(video._id)}
                  >
                    Remove
                  </button>
                </div>
              )}
              {removingId === video._id && (
                <div className="space-y-2">
                  <p>Remove this video from the creator page and feed?</p>
                  <button
                    type="button"
                    disabled={deleting}
                    onClick={() => {
                      void remove(video._id)
                    }}
                  >
                    Confirm removal
                  </button>
                  <button
                    type="button"
                    className="ml-4"
                    onClick={() => setRemovingId(undefined)}
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
        {canEdit && !showForm && (
          <Button
            label="Add product video"
            type="button"
            onClick={() => editVideo()}
          />
        )}
        {canEdit && showForm && (
          <form
            onSubmit={(event) => {
              void save(event)
            }}
            className="rounded-xl border p-4 space-y-4"
          >
            <fieldset disabled={saving} className="space-y-4">
              <TextInput
                title="Video title"
                hasIcon={false}
                required
                maxLength={300}
                value={form.title}
                onChange={(event) =>
                  setForm({ ...form, title: event.target.value })
                }
              />
              <TextInput
                title="YouTube URL or video ID"
                hasIcon={false}
                required
                value={form.youtubeId}
                onChange={(event) =>
                  setForm({ ...form, youtubeId: event.target.value })
                }
              />
              <div className="space-y-2">
                <p className="font-medium">Linked products</p>
                {form.productIds.map((id) => (
                  <div key={id} className="flex gap-3 items-center">
                    <span>
                      {products.find((product) => product._id === id)?.name ??
                        'Product no longer available'}
                    </span>
                    <button
                      type="button"
                      className="text-danger"
                      onClick={() =>
                        setForm({
                          ...form,
                          productIds: form.productIds.filter(
                            (value) => value !== id,
                          ),
                        })
                      }
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <Button
                  label="Select products"
                  type="button"
                  disabled={form.productIds.length >= 20}
                  onClick={() => setSelecting(true)}
                />
              </div>
              <TextInput
                title="Display order"
                hasIcon={false}
                required
                type="number"
                min={0}
                step={1}
                value={String(form.displayOrder)}
                onChange={(event) =>
                  setForm({ ...form, displayOrder: Number(event.target.value) })
                }
              />
              <label className="flex gap-2">
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(event) =>
                    setForm({ ...form, isActive: event.target.checked })
                  }
                />
                Active
              </label>
              <div className="flex gap-3">
                <Button
                  label={saving ? 'Saving…' : 'Save video'}
                  primary
                  type="submit"
                />
                <Button
                  label="Cancel"
                  type="button"
                  onClick={() => setShowForm(false)}
                />
              </div>
            </fieldset>
          </form>
        )}
        <SelectProductModal
          open={selecting}
          setOpen={setSelecting}
          onCloseCallback={() => setSelecting(false)}
          selectedProducts={[]}
          excludedProducts={form.productIds}
          maxSelection={20 - form.productIds.length}
          query={{ vendorId: creatorId }}
          onSuccessCallback={(selected) => {
            setForm({
              ...form,
              productIds: Array.from(
                new Set([
                  ...form.productIds,
                  ...selected.map((product) => product._id),
                ]),
              ).slice(0, 20),
            })
            setProducts([
              ...products,
              ...selected.map((product) => ({
                _id: product._id,
                name: product.name,
              })),
            ])
            setSelecting(false)
          }}
        />
      </div>
    </Accordion>
  )
}
